import { sortDateAsc } from '@strata/helper-functions';
import { CurrentBid } from '@strata/react-current-bid';
import { Grid, PrimaryData, SecondaryData } from '@strata/react-grid';
import { ServicingRate } from '@strata/react-servicing-rate';
import PropTypes from 'prop-types';
import { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { useGetLiveReferralDetailsQuery } from 'store/api/auctionApi';
import { useGetAllSuppliersQuery } from 'store/api/referenceApi';
import { Grids, useGridState } from 'store/grids/gridsSlice';
import { fetchReferralBids } from 'store/referrals/referralsSlice';

const initialSortProperties = [{ id: 'bids', desc: true }];

const columns = [
    {
        accessorKey: 'name',
        header: 'Name',
        size: 160,
        cell: ({ getValue }) => <PrimaryData data={getValue()} />,
    },
    {
        accessorKey: 'npi',
        header: 'NPI',
    },
    {
        accessorKey: 'bids',
        header: 'Bids',
    },
    {
        accessorKey: 'defaultRate',
        header: 'Default Rate',
        cell: ({ getValue, row }) => (
            <SecondaryData
                data={
                    <ServicingRate
                        servicingRate={getValue()}
                        referentPriceType={row.original.referentPriceType}
                    />
                }
            />
        ),
    },
    {
        accessorKey: 'winningBid',
        header: 'Winning Bid',
        cell: ({ getValue, row }) => {
            const getBidState = () => {
                if (row.original.bids === 0) {
                    return CurrentBid.states.NO_BID;
                }
                return row.original.isWinner ? CurrentBid.states.WON : CurrentBid.states.LOST;
            };
            return (
                <SecondaryData
                    data={
                        <CurrentBid
                            bidAmount={getValue()}
                            referentPriceType={row.original.referentPriceType}
                            bidState={getBidState()}
                        />
                    }
                />
            );
        },
    },
    {
        accessorKey: 'maxBid',
        header: 'Max Bid',
        cell: ({ getValue, row }) => (
            <SecondaryData
                data={
                    <CurrentBid
                        bidAmount={getValue()}
                        referentPriceType={row.original.referentPriceType}
                    />
                }
            />
        ),
    },
    {
        accessorKey: 'lostBy',
        header: 'Lost By',
        cell: ({ getValue }) => (
            <SecondaryData data={<ServicingRate servicingRate={getValue()} />} />
        ),
    },
];

export const PharmacyActivity = ({ referralDetails = undefined }) => {
    const dispatch = useDispatch();

    const bids = useSelector((state) => state.referrals.bids[referralDetails?.referralId]);
    useEffect(() => {
        if (referralDetails?.referralId) {
            dispatch(
                fetchReferralBids({
                    referralId: referralDetails.referralId,
                    payerId: referralDetails.payerId,
                    auctionStart: referralDetails.auctionStart,
                    auctionEnd: referralDetails.auctionEnd,
                }),
            );
        }
    }, [dispatch, referralDetails]);

    const { data: auctionDetails, isFetching: isAuctionDetailsFetching } =
        useGetLiveReferralDetailsQuery(
            {
                referralId: referralDetails?.referralId,
                auctionEnd: referralDetails?.auctionEnd,
            },
            { skip: !referralDetails },
        );

    const { data: allSuppliersData } = useGetAllSuppliersQuery();
    const { supplierMap } = allSuppliersData ?? {};

    // Format the raw bid data into the expected grid format
    const bidData = useMemo(() => {
        // Count the number of bids made by each supplier and track their best bid
        const countsAndBestBids = {};

        // Populate the list of counts and best bids with all the marketplace participants
        auctionDetails?.marketplaceParticipants?.forEach((participantNpi) => {
            countsAndBestBids[participantNpi] = {
                bidCount: 0,
                bestBid: undefined,
                bestBidTimestamp: undefined,
            };
        });

        bids?.forEach((bid) => {
            if (countsAndBestBids[bid.supplierNpi]) {
                countsAndBestBids[bid.supplierNpi].bidCount += 1;
                if (bid.bidAmt < (countsAndBestBids[bid.supplierNpi].bestBid ?? 1)) {
                    countsAndBestBids[bid.supplierNpi].bestBid = bid.bidAmt;
                    countsAndBestBids[bid.supplierNpi].bestBidTimestamp = bid.bidTimestamp;
                }
            } else {
                countsAndBestBids[bid.supplierNpi] = {
                    bidCount: 1,
                    bestBid: bid.bidAmt,
                    bestBidTimestamp: bid.bidTimestamp,
                };
            }
        });

        // Sort the supplier bids
        const sortedTopBids = Object.entries(countsAndBestBids)
            .filter(([_supplierNpi, supplierBidInfo]) => supplierBidInfo.bidCount > 0)
            .map(([supplierNpi, supplierBidInfo]) => ({
                supplierNpi,
                bidAmt: supplierBidInfo.bestBid,
                bidTimestamp: supplierBidInfo.bestBidTimestamp,
            }))
            .sort((a, b) =>
                a.bidAmt === b.bidAmt
                    ? sortDateAsc(a.bidTimestamp, b.bidTimestamp)
                    : a.bidAmt - b.bidAmt,
            );

        // Determine the winning bid
        let winningBid;
        let winningSupplierNpi;
        if (sortedTopBids.length > 0) {
            winningSupplierNpi = sortedTopBids[0].supplierNpi;

            if (sortedTopBids.length === 1) {
                // Case where there was only one bid
                if (referralDetails?.winningBidRate !== undefined) {
                    // Use referral winning bid rate if present
                    winningBid = referralDetails.winningBidRate;
                } else if (auctionDetails?.defaultRate) {
                    // Use auction default rate if present
                    winningBid = Number(auctionDetails.defaultRate) - 0.0001;
                }
            } else if (sortedTopBids[0].bidAmt === sortedTopBids[1].bidAmt) {
                // The top two bids are tied
                winningBid = sortedTopBids[1].bidAmt;
            } else {
                // The top two bids are not tied
                winningBid = sortedTopBids[1].bidAmt - 0.0001;
            }
        }

        return Object.entries(countsAndBestBids).map(([supplierNpi, supplierBidInfo]) => {
            const isWinner = supplierNpi === winningSupplierNpi;
            return {
                name: supplierMap?.[supplierNpi]?.name,
                npi: supplierNpi,
                bids: supplierBidInfo.bidCount,
                defaultRate: referralDetails?.defaultRate,
                referentPriceType: referralDetails?.referentPriceType,
                maxBid: supplierBidInfo.bestBid,
                isWinner,
                winningBid: isWinner ? winningBid : supplierBidInfo.bestBid,
                lostBy:
                    !isWinner && winningBid && supplierBidInfo.bestBid
                        ? winningBid - supplierBidInfo.bestBid
                        : undefined,
            };
        });
    }, [bids, auctionDetails, referralDetails, supplierMap]);

    const { sort, updateGridSort } = useGridState(Grids.BIDS, initialSortProperties);

    return (
        <Grid
            data={bidData}
            columns={columns}
            isLoading={!bids || isAuctionDetailsFetching}
            enableSorting
            sortProperties={sort}
            onSort={updateGridSort}
        />
    );
};

PharmacyActivity.propTypes = {
    referralDetails: PropTypes.shape({
        referralId: PropTypes.string,
        payerId: PropTypes.string,
        auctionStart: PropTypes.string,
        auctionEnd: PropTypes.string,
        defaultRate: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        winningBidRate: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        referentPriceType: PropTypes.string,
    }),
};
